<template>
  <div>
    <add-or-update-member-type-modal
      :is-add-or-update-active.sync="isAddOrUpdateActive"
      :update-data="updateData"
      @refetchData="fetchAllData"
      @resetUpdateData="resetUpdateData"
    />

    <list-table
      :records="records"
      :columns="tableColumns"
      :total-records="totalRecords"
      :current-page-number.sync="currentPage"
      :search-filter.sync="search"
      :loading="loading"
      :error-message="errorMessage"
      :show-search="true"
      :show-filter-by-default="true"
    >
      <template #filters>
        <b-row>
          <b-col cols="12" md="3" class="mb-1 mb-md-0">
            <v-select
              v-model="ministerFilter"
              placeholder="COP minister"
              :clearable="false"
              :options="flagFilterOptions('Ministers')"
              :reduce="(val) => val.value"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            />
          </b-col>
          <b-col cols="12" md="3" class="mb-1 mb-md-0">
            <v-select
              v-model="staffFilter"
              placeholder="COP staff"
              :clearable="false"
              :options="flagFilterOptions('Staff')"
              :reduce="(val) => val.value"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            />
          </b-col>
          <b-col v-if="hasActiveFilters" cols="12" md="3">
            <b-button variant="flat-secondary" @click="resetFilters">
              <feather-icon icon="XIcon" class="mr-50" />
              <span class="align-middle">Clear filters</span>
            </b-button>
          </b-col>
        </b-row>
      </template>

      <template v-if="can('member_types:CanAddNew')" #action_button>
        <b-button
          v-ripple.400="'rgba(113, 102, 240, 0.15)'"
          variant="primary"
          @click="isAddOrUpdateActive = true"
        >
          <span class="align-middle text-lg">Add Type</span>
        </b-button>
      </template>

      <template #cell(is_cop_minister)="data">
        <b-badge pill :variant="data.item.is_cop_minister ? 'light-success' : 'light-secondary'">
          {{ data.item.is_cop_minister ? 'Yes' : 'No' }}
        </b-badge>
      </template>

      <template #cell(is_cop_staff)="data">
        <b-badge pill :variant="data.item.is_cop_staff ? 'light-success' : 'light-secondary'">
          {{ data.item.is_cop_staff ? 'Yes' : 'No' }}
        </b-badge>
      </template>

      <template #cell(titles_count)="data">
        <b-badge variant="light-info" class="cursor-pointer" @click="toggleChildren(data.item, 'titles')">
          <feather-icon icon="BookOpenIcon" class="mr-25" size="14" />
          {{ data.item.titles_count }} {{ data.item.titles_count === 1 ? 'title' : 'titles' }}
        </b-badge>
      </template>

      <template #cell(designations_count)="data">
        <b-badge variant="light-warning" class="cursor-pointer" @click="toggleChildren(data.item, 'designations')">
          <feather-icon icon="AwardIcon" class="mr-25" size="14" />
          {{ data.item.designations_count }} {{ data.item.designations_count === 1 ? 'designation' : 'designations' }}
        </b-badge>
      </template>

      <template #cell(created_by_user)="data">
        <div v-if="data.item.created_by_user" class="d-flex flex-column">
          <small class="font-weight-bold">{{ data.item.created_by_user.first_name }} {{ data.item.created_by_user.last_name }}</small>
          <small class="text-muted">{{ formatDate(data.item.created, 'dd MMM yyyy, h:mm a') }}</small>
        </div>
        <small v-else class="text-muted">—</small>
      </template>

      <template #cell(updated_by_user)="data">
        <div v-if="data.item.updated_by_user" class="d-flex flex-column">
          <small class="font-weight-bold">{{ data.item.updated_by_user.first_name }} {{ data.item.updated_by_user.last_name }}</small>
          <small class="text-muted">{{ formatDate(data.item.modified, 'dd MMM yyyy, h:mm a') }}</small>
        </div>
        <small v-else class="text-muted">—</small>
      </template>

      <template #cell(actions)="data">
        <b-dropdown
          variant="link"
          no-caret
          :right="$store.state.appConfig.isRTL"
        >
          <template #button-content>
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </template>

          <b-dropdown-item v-if="can('member_types:CanUpdate')" @click="onEdit(data.item)">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">Edit</span>
          </b-dropdown-item>

          <b-dropdown-item v-if="can('member_types:CanDelete')" @click="onDelete(data.item._id)">
            <feather-icon icon="DeleteIcon" />
            <span class="align-middle ml-50">Delete</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>

      <template #row-details="data">
        <b-card class="mb-1 border" no-body>
          <b-card-header class="py-75 px-1 bg-light-primary">
            <div class="d-flex align-items-center justify-content-between">
              <h6 class="mb-0 text-primary">
                <feather-icon :icon="expandedChildType === 'titles' ? 'BookOpenIcon' : 'AwardIcon'" class="mr-50" />
                {{ expandedChildType === 'titles' ? 'Titles' : 'Designations' }} under "{{ data.item.title }}"
              </h6>
              <b-button variant="flat-secondary" size="sm" @click="$set(data.item, '_showDetails', false)">
                <feather-icon icon="XIcon" />
              </b-button>
            </div>
          </b-card-header>
          <b-card-body class="p-1">
            <b-overlay :show="childrenLoading" opacity="0.5">
              <b-table
                v-if="childRecords.length"
                :items="childRecords"
                :fields="childColumns"
                small
                bordered
                responsive
                show-empty
                class="mb-0"
              >
                <template #cell(created_by_user)="child">
                  <small v-if="child.item.created_by_user">
                    {{ child.item.created_by_user.first_name }} {{ child.item.created_by_user.last_name }}
                  </small>
                  <small v-else class="text-muted">—</small>
                </template>
              </b-table>
              <p v-else class="text-muted mb-0 text-center py-1">
                No {{ expandedChildType }} found for this type.
              </p>
            </b-overlay>
          </b-card-body>
        </b-card>
      </template>
    </list-table>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import AddOrUpdateMemberTypeModal from "@/@core/components/shared/church/AddOrUpdateMemberTypeModal.vue";

import { debounce, get } from "lodash";
import { formatDate } from "@core/utils/utils";
import {
  BLink, BButton, BModal, BRow, BCol, BTooltip, BAvatar, BMedia, BBadge,
  BDropdown, BDropdownItem, BCard, BCardHeader, BCardBody, BTable, BOverlay
} from "bootstrap-vue";

const watchHandler = {
  handler(){
    this.debounceFetchAllData()
  },
  immediate: false
}

const watchHandlerRequiresPageReset = {
  handler(){
    this.currentPage = 1;
    this.debounceFetchAllData()
  },
  immediate: false
}

export default {
  components: {
    BRow,
    BCol,
    BLink,
    BModal,
    BMedia,
    BBadge,
    BButton,
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BOverlay,
    vSelect,
    BAvatar,
    BTooltip,
    ListTable,
    BDropdown,
    BDropdownItem,
    AddOrUpdateMemberTypeModal
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: false,
      errorMessage: "",
      records: [],

      ministerFilter: "all",
      staffFilter: "all",

      search: "",
      updateData: null,
      debounceFetchAllData: null,
      isAddOrUpdateActive: false,

      // Children expand state
      childrenLoading: false,
      childRecords: [],
      expandedChildType: null,

      tableColumns: [
        { key: 'title', label: "Type", sortable: false },
        { key: 'is_cop_minister', label: 'COP Minister', sortable: false },
        { key: 'is_cop_staff', label: 'COP Staff', sortable: false },
        { key: 'titles_count', label: 'Titles', sortable: false },
        { key: 'designations_count', label: 'Designations', sortable: false },
        { key: 'created_by_user', label: 'Created By', sortable: false },
        { key: 'updated_by_user', label: 'Updated By', sortable: false },
        { key: 'actions' },
      ]
    }
  },
  computed: {
    hasActiveFilters() {
      return this.ministerFilter !== "all" || this.staffFilter !== "all";
    },
    childColumns() {
      if (this.expandedChildType === 'titles') {
        return [
          { key: 'title', label: 'Title', sortable: false },
          { key: 'created_by_user', label: 'Created By', sortable: false },
        ]
      }
      return [
        { key: 'designation', label: 'Designation', sortable: false },
        { key: 'is_cop_area_head', label: 'Area Head', sortable: false, formatter: (val) => (val ? 'Yes' : 'No') },
        { key: 'created_by_user', label: 'Created By', sortable: false },
      ]
    },
    perPage: {
      get() {
        return this.$store.getters[`navigation/recordsPerPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_RECORDS_PER_PAGE`, value)
      }
    },
    currentPage: {
      get() {
        return this.$store.getters[`navigation/currentPage`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_CURRENT_PAGE_NUMBER`, value)
      }
    },
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  watch: {
    currentPage: watchHandler,
    ministerFilter: watchHandlerRequiresPageReset,
    staffFilter: watchHandlerRequiresPageReset,
    search: watchHandlerRequiresPageReset,
    perPage: watchHandlerRequiresPageReset,
  },
  created() {
    this.debounceFetchAllData = debounce(this.fetchAllData, 500);

    const {
      search = "",
      page = 1,
      limit = this.perPage,
      isCopMinister,
      isCopStaff,
    } = this.$route.query;

    this.search = search;
    this.perPage = +limit;
    this.currentPage = +page;
    this.ministerFilter = this.flagFromQuery(isCopMinister);
    this.staffFilter = this.flagFromQuery(isCopStaff);

    this.debounceFetchAllData();
  },
  methods: {
    formatDate,
    flagFilterOptions(label) {
      return [
        { label: `All ${label.toLowerCase()} settings`, value: "all" },
        { label: `${label} only`, value: "yes" },
        { label: `Excluding ${label.toLowerCase()}`, value: "no" },
      ]
    },
    flagFromQuery(value) {
      if (value === "true") return "yes";
      if (value === "false") return "no";
      return "all";
    },
    flagToQuery(value) {
      if (value === "yes") return "true";
      if (value === "no") return "false";
      return undefined;
    },
    resetFilters() {
      this.ministerFilter = "all";
      this.staffFilter = "all";
    },
    async toggleChildren(item, type) {
      // If already expanded with this type, collapse
      if (item._showDetails && this.expandedChildType === type) {
        this.$set(item, '_showDetails', false);
        return;
      }

      // Collapse any other expanded row
      this.records.forEach(r => this.$set(r, '_showDetails', false));

      this.expandedChildType = type;
      this.$set(item, '_showDetails', true);

      try {
        this.childrenLoading = true;
        const query = { member_type_id: item._id, limit: 1000, page: 1 };

        if (type === 'titles') {
          const res = await this.useJwt().mainService.memberTitles.list(query);
          const { data } = res.data;
          // Flatten grouped data — titles come grouped by member_type
          this.childRecords = (data || []).reduce((acc, group) => acc.concat(group.categories || []), []);
        } else {
          const res = await this.useJwt().mainService.memberDesignations.list(query);
          const { data } = res.data;
          this.childRecords = (data || []).reduce((acc, group) => acc.concat(group.categories || []), []);
        }
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message,
          },
        });
        this.childRecords = [];
      } finally {
        this.childrenLoading = false;
      }
    },
    async fetchAllData() {
      try {
        this.loading = true;
        this.errorMessage = "";

        const query = {
          limit: this.perPage,
          search: this.search || undefined,
          page: this.currentPage,
          isCopMinister: this.flagToQuery(this.ministerFilter),
          isCopStaff: this.flagToQuery(this.staffFilter),
        }
        await this.$router.push({ query }).catch(() => {});

        const request = await this.useJwt().mainService.memberTypes.list(query);
        const { data, pagination } = request.data;

        this.totalRecords = pagination.totalRecords;
        this.perPage = pagination.limit;
        this.records = data;
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },

    async onDelete(id){
      try {
        this.loading = true;

        const result = await this.$swal({
          title: 'Confirm Delete?',
          text: "Do you want to delete this record?",
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });

        if (!result.value) {
          return;
        }

        await this.useJwt().mainService.memberTypes.delete(id);
        await this.fetchAllData()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: 'Record deleted Successfully'
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message;

        this.$nextTick(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error!',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error_message
            },
          });
        })
      } finally {
        this.loading = false;
      }
    },
    onEdit(update){
      this.updateData = update;
      this.isAddOrUpdateActive = true;
    },
    resetUpdateData(){
      if (this.updateData){
        this.updateData = null
      }
    },
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
